import { FlexSpinner } from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldProps } from 'formik'
import {
  FormikCheckbox,
  FormikDropdown,
  FormikTextField,
  mapFieldToTextField,
} from 'formik-office-ui-fabric-react'
import { Box } from 'grid'
import { getDropdownOptionsForEnum } from 'helpers'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { MailTemplateType, useProvingGroundDropdownQuery } from 'model'
import { ITag, Label, Stack, TextField } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FieldStack, FieldStackItem, FormStack } from '../FormStack'

export interface MailTemplateFormValues {
  name: string
  provingGroundId: string | null
  to: string | undefined
  cc: string | undefined
  subject: string
  body: string
  type: MailTemplateType
  inactive: boolean
}

export class MailTemplateFormSchema {
  constructor() {
    return Yup.object().shape({
      name: Yup.string().required().trim(),
    })
  }
}

export const MailTemplateForm: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const formikContext = useCurrentFormik()

  const { loading: pgLoading, data: pgData } = useProvingGroundDropdownQuery()
  const provingGrounds =
    !pgLoading && pgData && pgData.provingGrounds ? pgData.provingGrounds : []

  const provingGroundOptions: ITag[] = [
    { key: '', name: t('model.mailTemplate.allProvingGrounds') },
    ...provingGrounds.map(
      (pg) =>
        ({
          key: pg._id,
          name: pg.title,
        } as ITag)
    ),
  ]

  const mailTemplateTypeOptions = getDropdownOptionsForEnum(
    t,
    MailTemplateType,
    'mailTemplateType'
  )

  const [selectedTemplateType, setSelectedTemplateType] = React.useState(
    formikContext.values.type as MailTemplateType
  )

  return (
    <FlexSpinner loading={pgLoading}>
      <FormStack>
        <FieldStack>
          <FieldStackItem
            name="type"
            render={(fieldProps) => (
              <>
                <Label>{t('field.mailTemplate.type')}</Label>
                <FormikDropdown
                  options={mailTemplateTypeOptions}
                  onChanged={(option) => {
                    if (option) {
                      setSelectedTemplateType(option.key as MailTemplateType)
                    }
                  }}
                  {...fieldProps}
                />
              </>
            )}
          />
          <FieldStackItem
            name="name"
            label={t('field.mailTemplate.name')}
            component={FormikTextField}
          />
          <FieldStackItem
            name="provingGroundId"
            render={(fieldProps) => (
              <>
                <Label required={true}>{t('field.provingGround.label')}</Label>
                <FormikTagPicker {...fieldProps} tags={provingGroundOptions} />
              </>
            )}
          />
          {selectedTemplateType !== MailTemplateType.TaxRequirements && (
            <>
              <FieldStackItem
                name="to"
                label={t('field.mailTemplate.to')}
                component={FormikTextField}
              />
              <FieldStackItem
                name="cc"
                label={t('field.mailTemplate.cc')}
                component={FormikTextField}
              />
            </>
          )}
          <FieldStackItem
            name="subject"
            label={t('field.mailTemplate.subject')}
            component={FormikTextField}
          />
          <FieldStackItem
            name="body"
            required={true}
            render={(fieldProps: FieldProps<MailTemplateFormValues>) => (
              <TextField
                label={t('field.mailTemplate.body')}
                multiline={true}
                autoAdjustHeight={true}
                {...mapFieldToTextField(fieldProps)}
              />
            )}
          />
          <FieldStack>
            <Box py={4}>
              <FieldStackItem
                stack={{ grow: 0 }}
                name="inactive"
                render={(fieldProps) => (
                  <>
                    <Label>{t('field.inactive.label')}</Label>
                    <Box py={1}>
                      <FormikCheckbox {...fieldProps} />
                    </Box>
                  </>
                )}
              />
            </Box>
          </FieldStack>
          {selectedTemplateType === MailTemplateType.TaxOfficeChanges ? (
            <Stack.Item>
              {t('model.mailTemplate.taxOfficeTemplateHint')}
            </Stack.Item>
          ) : (
            <Stack.Item>
              <Label>
                Folgende Variablen stehen für den Inhaltsbereich der Vorlage zur
                Verfügung
              </Label>
              <table>
                <tr>
                  <th align="left" style={{ width: 100 }}>
                    Variable
                  </th>
                  <th align="left" style={{ width: 200 }}>
                    Beschreibung
                  </th>
                </tr>
                <tr>
                  <td align="left">[ErprobungsNr]</td>
                  <td align="left">Erprobungsnummer</td>
                </tr>
                <tr>
                  <td align="left">[Erprobung]</td>
                  <td align="left">Erprobungstitel</td>
                </tr>
                <tr>
                  <td align="left">[ErprobungDatumVon]</td>
                  <td align="left">Erprobung Datum Von</td>
                </tr>
                <tr>
                  <td align="left">[ErprobungDatumBis]</td>
                  <td align="left">Erprobung Datum Bis</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerAusweisfreischaltung]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Ausweisfreischaltung
                  </td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerFlugbuchung]</td>
                  <td align="left">Liste aller Teilnehmer zur Flugbuchung</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerFähre]</td>
                  <td align="left">Liste aller Teilnehmer zur Fährbuchung</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerMietwagen]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Mietwagenbuchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[Hotelname]</td>
                  <td align="left">Name der Unterkunft</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerUnterkunft]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Unterkunftbuchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin1Name]</td>
                  <td align="left">Name des Streckenhotels Hin 1</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin1]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin1 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin2Name]</td>
                  <td align="left">Name des Streckenhotels Hin 2</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin2]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin2 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin3Name]</td>
                  <td align="left">Name des Streckenhotels Hin 3</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin3]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin3 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin4Name]</td>
                  <td align="left">Name des Streckenhotels Hin 4</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin4]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin4 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin5Name]</td>
                  <td align="left">Name des Streckenhotels Hin 5</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin5]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin5 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin6Name]</td>
                  <td align="left">Name des Streckenhotels Hin 6</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin6]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin6 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin7Name]</td>
                  <td align="left">Name des Streckenhotels Hin 7</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin7]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin7 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin8Name]</td>
                  <td align="left">Name des Streckenhotels Hin 8</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin8]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin8 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin9Name]</td>
                  <td align="left">Name des Streckenhotels Hin 9</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin9]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin9 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelHin10Name]</td>
                  <td align="left">Name des Streckenhotels Hin 10</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelHin10]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Hin10 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück1Name]</td>
                  <td align="left">Name des Streckenhotels Rück 1</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück1]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück1 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück2Name]</td>
                  <td align="left">Name des Streckenhotels Rück 2</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück2]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück2 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück3Name]</td>
                  <td align="left">Name des Streckenhotels Rück 3</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück3]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück3 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück4Name]</td>
                  <td align="left">Name des Streckenhotels Rück 4</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück4]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück4 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück5Name]</td>
                  <td align="left">Name des Streckenhotels Rück 5</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück5]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück5 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück6Name]</td>
                  <td align="left">Name des Streckenhotels Rück 6</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück6]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück6 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück7Name]</td>
                  <td align="left">Name des Streckenhotels Rück 7</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück7]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück7 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück8Name]</td>
                  <td align="left">Name des Streckenhotels Rück 8</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück8]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück8 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück9Name]</td>
                  <td align="left">Name des Streckenhotels Rück 9</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück9]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück9 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[SteckenhotelRück10Name]</td>
                  <td align="left">Name des Streckenhotels Rück 10</td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerStreckenhotelRück10]</td>
                  <td align="left">
                    Liste aller Teilnehmer zur Streckenhotel Rück10 Buchung
                  </td>
                </tr>
                <tr>
                  <td align="left">[TeilnehmerFreieMail]</td>
                  <td align="left">
                    Liste aller ausgewählten Teilnehmer ohne speziellen Kontext
                  </td>
                </tr>
                <tr>
                  <td align="left">[Antragsteller]</td>
                  <td align="left">Antragsteller (Vorname Nachname)</td>
                </tr>
                <tr>
                  <td align="left">[AntragstellerMail]</td>
                  <td align="left">Antragsteller E-Mail</td>
                </tr>
                <tr>
                  <td align="left">[NachnameAntragsteller]</td>
                  <td align="left">Nachname des Antragstellers</td>
                </tr>
                <tr>
                  <td align="left">[Verantwortlicher]</td>
                  <td align="left">Verantwortlicher (Vorname Nachname)</td>
                </tr>
                <tr>
                  <td align="left">[NachnameVerantwortlicher]</td>
                  <td align="left">Nachname des Verantwortlichen</td>
                </tr>
                <tr>
                  <td align="left">[VerantwortlicherMail]</td>
                  <td align="left">Verantwortlicher E-Mail</td>
                </tr>
                <tr>
                  <td align="left">[NachnameStellvertreter]</td>
                  <td align="left">Nachname des Stellvertreters</td>
                </tr>
                <tr>
                  <td align="left">[StellvertreterMail]</td>
                  <td align="left">Stellvertreter E-Mail</td>
                </tr>
                <tr>
                  <td align="left">[Standort]</td>
                  <td align="left">Standort Klartext</td>
                </tr>
                <tr>
                  <td align="left">[StandortKürzel]</td>
                  <td align="left">Standort Kürzel</td>
                </tr>
                <tr>
                  <td align="left">[Land]</td>
                  <td align="left">Standort Länderkürzel</td>
                </tr>
                <tr>
                  <td align="left">[Footer]</td>
                  <td align="left">
                    Signatur des aktuell angemeldeten Nutzers
                  </td>
                </tr>
              </table>
            </Stack.Item>
          )}
        </FieldStack>
      </FormStack>
    </FlexSpinner>
  )
}
